import React, { useContext, useEffect, useState } from 'react';
import { Container, Stack, Form } from 'react-bootstrap';
import { IconClock } from '@tabler/icons-react';
import moment from 'moment';
import { OrganizationContext } from '../../../../context/organization_context/OrganizationContext';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import { ToastType, useToast } from '../../../../context/toast/ToastContext';
import AppSidebar from '../../../../components/navbar/DesktopAppSidebar';
import { Button, Box, Typography, Card, CardContent, Divider, Grid } from '@mui/material';
import { useUserContext } from '../../../../context/user_context/UserContext';
import { clockIn, clockOut, timeClockEntriesSnapshotListener, fetchUserWeeklyTimeClocks } from '../../../../repository/TimeClockDAO';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Loading } from '../../../../components/loading/Loading';
import BaseModal from '../../../../components/modals/BaseModal';

const DailyEntriesDialog = ({ show, onClose, date, entries }) => {
    const { t } = useTranslation();

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        return moment(timestamp.seconds * 1000).format('h:mm A');
    };

    const calculateDuration = (entry) => {
        if (!entry.timestamp_out) return null;
        const duration = moment.duration(
            moment(entry.timestamp_out.seconds * 1000).diff(
                moment(entry.timestamp_in.seconds * 1000)
            )
        );
        return {
            hours: Math.floor(duration.asHours()),
            minutes: duration.minutes()
        };
    };

    return (
        <BaseModal
            open={show}
            onClose={onClose}
            title={moment(date).format('dddd, MMMM D, YYYY')}
        >
            <Box sx={{ minWidth: 300 }}>
                {entries.length === 0 ? (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                        {t(StringKeys.NO_ENTRIES_FOR_THIS_DAY)}
                    </Typography>
                ) : (
                    entries.map((entry, index) => {
                        const duration = calculateDuration(entry);
                        return (
                            <Box 
                                key={entry.id || index} 
                                sx={{ 
                                    py: 2,
                                    borderBottom: index < entries.length - 1 ? '1px solid #eee' : 'none'
                                }}
                            >
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography>
                                                {formatTimestamp(entry.timestamp_in)} - {' '}
                                                {entry.timestamp_out ? formatTimestamp(entry.timestamp_out) : t(StringKeys.CURRENTLY_CLOCKED_IN)}
                                            </Typography>
                                            {duration && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {duration.hours}h {duration.minutes}m
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        );
                    })
                )}
            </Box>
        </BaseModal>
    );
};

function TimeClock() {
    const { organization } = useContext(OrganizationContext);
    const { firebaseUser } = useUserContext();
    const [currentDateTime, setCurrentDateTime] = useState(moment());
    const [selectedWeek, setSelectedWeek] = useState(moment().startOf('week'));
    const { setLoadingData } = useLoadingContext();
    const { showToast } = useToast();
    const [lastEntry, setLastEntry] = useState(null);
    const [weeklyEntries, setWeeklyEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { t } = useTranslation();
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedDayEntries, setSelectedDayEntries] = useState([]);
    const [showDailyDialog, setShowDailyDialog] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentDateTime(moment());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (firebaseUser && organization) {
            const statusUnsubscribe = timeClockEntriesSnapshotListener(
                organization.id, 
                firebaseUser.uid, 
                (entriesData) => {
                    const sortedEntries = entriesData.entries.sort((a, b) => b.timestamp_in.seconds - a.timestamp_in.seconds);
                    const latestEntry = sortedEntries[0];
                    setLastEntry(latestEntry || null);
                }
            );

            const weekString = selectedWeek.clone().format('YYYY-MM-DD');
            const weeklyUnsubscribe = fetchUserWeeklyTimeClocks(
                organization.id,
                firebaseUser.uid,
                weekString,
                setWeeklyEntries,
                setError,
                setLoading
            );

            return () => {
                statusUnsubscribe();
                weeklyUnsubscribe();
            };
        }
    }, [organization, firebaseUser, selectedWeek]);

    const handleClockIn = async () => {
        if (!lastEntry || lastEntry.timestamp_out) {
            setLoadingData(true);
            try {
                const result = await clockIn(organization.id, firebaseUser.uid);
                if (result.success) {
                    showToast(t(StringKeys.SUCCESS), ToastType.SUCCESS);
                } else {
                    console.error("Clock-in failed:", result.error);
                    showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
                }
            } catch (error) {
                console.error("Unexpected error clocking in:", error);
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
            } finally {
                setLoadingData(false);
            }
        }
    };

    const handleClockOut = async () => {
        if (lastEntry && !lastEntry.timestamp_out) {
            setLoadingData(true);
            try {
                const result = await clockOut(organization.id, firebaseUser.uid, lastEntry.id);
                if (result.success) {
                    showToast(t(StringKeys.SUCCESS), ToastType.SUCCESS);
                } else {
                    console.error("Clock-out failed:", result.error);
                    showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
                }
            } catch (error) {
                console.error("Unexpected error clocking out:", error);
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
            } finally {
                setLoadingData(false);
            }
        }
    };

    const handleWeekChange = (newDate) => {
        if (newDate && moment.isMoment(newDate)) {
            setSelectedWeek(newDate.clone().startOf('week'));
        }
    };

    const formatWeekDisplay = (start, end) => {
        if (start.month() === end.month()) {
            return `${start.format('MMM D')} - ${end.format('D, YYYY')}`;
        } else if (start.year() === end.year()) {
            return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
        } else {
            return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
        }
    };

    const calculateDailyTotal = (date) => {
        const dayEntries = weeklyEntries.filter(entry => {
            const entryDate = moment(entry.timestamp_in.seconds * 1000);
            return entryDate.isSame(date, 'day');
        });

        let totalMinutes = 0;
        dayEntries.forEach(entry => {
            if (entry.timestamp_in && entry.timestamp_out) {
                const duration = moment.duration(
                    moment(entry.timestamp_out.seconds * 1000).diff(
                        moment(entry.timestamp_in.seconds * 1000)
                    )
                );
                totalMinutes += duration.asMinutes();
            }
        });

        return {
            hours: Math.floor(totalMinutes / 60),
            minutes: Math.round(totalMinutes % 60),
            isActive: dayEntries.some(entry => !entry.timestamp_out),
            totalMinutes
        };
    };

    const calculateWeeklyTotal = () => {
        let totalMinutes = weekDays.reduce((acc, { total }) => acc + total.totalMinutes, 0);
        return {
            hours: Math.floor(totalMinutes / 60),
            minutes: Math.round(totalMinutes % 60)
        };
    };

    const weekStart = selectedWeek.clone().startOf('week');
    const weekEnd = selectedWeek.clone().endOf('week');

    const weekDays = [...Array(7)].map((_, i) => {
        const date = weekStart.clone().add(i, 'days');
        const total = calculateDailyTotal(date);
        return { date, total };
    });

    const weeklyTotal = calculateWeeklyTotal();

    const handleDayClick = (date) => {
        const dayEntries = weeklyEntries.filter(entry => {
            const entryDate = moment(entry.timestamp_in.seconds * 1000);
            return entryDate.isSame(date, 'day');
        }).sort((a, b) => b.timestamp_in.seconds - a.timestamp_in.seconds);

        setSelectedDate(date);
        setSelectedDayEntries(dayEntries);
        setShowDailyDialog(true);
    };

    return (
        <AppSidebar
            content={
                <Container>
                    <Box sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} textAlign="center">
                                <IconClock size={48} />
                                <Typography variant="h5">{currentDateTime.format("dddd, MMMM Do YYYY")}</Typography>
                                <Typography variant="h6">{currentDateTime.format("h:mm:ss a")}</Typography>
                                <Stack direction="horizontal" gap={3} className="justify-content-center mt-4">
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        onClick={handleClockIn} 
                                        disabled={lastEntry && !lastEntry.timestamp_out}
                                    >
                                        {t(StringKeys.CLOCK_IN)}
                                    </Button>
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        onClick={handleClockOut} 
                                        disabled={!lastEntry || !!lastEntry.timestamp_out}
                                    >
                                        {t(StringKeys.CLOCK_OUT)}
                                    </Button>
                                </Stack>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ my: 3 }} />
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: { xs: 'column', md: 'row' },
                                    justifyContent: { md: 'space-between' },
                                    alignItems: { md: 'center' },
                                    gap: 2,
                                    mb: 2
                                }}>
                                    <Typography 
                                        variant="h6" 
                                        sx={{ 
                                            color: 'text.primary',
                                            fontWeight: 500
                                        }}
                                    >
                                        {t(StringKeys.YOUR_WEEKLY_TIME_CLOCK_ENTRIES)}
                                    </Typography>
                                    <Box sx={{ width: { xs: '100%', md: '400px' } }}>
                                        <LocalizationProvider dateAdapter={AdapterMoment}>
                                            <DatePicker
                                                label={t(StringKeys.SELECT_WEEK)}
                                                value={selectedWeek}
                                                onChange={handleWeekChange}
                                                format={formatWeekDisplay(weekStart, weekEnd)}
                                                views={['year', 'month', 'day']}
                                                sx={{ width: '100%' }}
                                            />
                                        </LocalizationProvider>
                                    </Box>
                                </Box>

                                {loading ? (
                                    <Loading />
                                ) : (
                                    <Card>
                                        <CardContent>
                                            <Grid container spacing={2}>
                                                {weekDays.map(({ date, total }) => (
                                                    <Grid item xs={12} sm key={date.format('YYYY-MM-DD')}>
                                                        <Box
                                                            sx={{
                                                                p: 2,
                                                                border: '1px solid #eee',
                                                                borderRadius: 1,
                                                                height: '100%',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                bgcolor: date.isSame(moment(), 'day') ? 'action.hover' : 'background.paper',
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    bgcolor: 'action.hover',
                                                                    transition: 'background-color 0.2s'
                                                                }
                                                            }}
                                                            onClick={() => handleDayClick(date)}
                                                        >
                                                            <Typography variant="subtitle2" color="text.secondary">
                                                                {date.format('ddd')}
                                                            </Typography>
                                                            <Typography variant="h6">
                                                                {date.format('D')}
                                                            </Typography>
                                                            <Box sx={{ mt: 1, textAlign: 'center' }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {total.hours}h {total.minutes}m
                                                                </Typography>
                                                                {total.isActive && (
                                                                    <Typography 
                                                                        variant="caption" 
                                                                        sx={{ 
                                                                            color: 'error.main',
                                                                            display: 'block',
                                                                            mt: 0.5
                                                                        }}
                                                                    >
                                                                        {t(StringKeys.CURRENTLY_CLOCKED_IN)}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                            <Box 
                                                sx={{ 
                                                    mt: 3, 
                                                    pt: 2, 
                                                    borderTop: '2px solid #eee',
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Typography variant="subtitle1" color="text.secondary" sx={{ mr: 1 }}>
                                                    {t(StringKeys.WEEKLY_TOTAL)}:
                                                </Typography>
                                                <Typography variant="h6">
                                                    {weeklyTotal.hours}h {weeklyTotal.minutes}m
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                    {selectedDate && (
                        <DailyEntriesDialog
                            show={showDailyDialog}
                            onClose={() => setShowDailyDialog(false)}
                            date={selectedDate}
                            entries={selectedDayEntries}
                        />
                    )}
                </Container>
            }
        />
    );
}

export default TimeClock;
